import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { Button } from 'antd';
import { saveAs } from 'file-saver';
import { ColumnsType } from 'antd/es/table';
import { DocumentType, IDocument } from './interfaces';
import { UploadCloudIcon } from '../Icons/UploadIcon';
import { NewTrashIcon } from '../Icons/TrashIcon';
import { NewDownloadIcon } from '../Icons/DownloadIcon';
import { useTheme } from '@mui/material';
import { Table } from './Table/styles';
import {
  StepHeader,
  StyledButton,
  StyledDownloadButton,
  StyledTableWrapper,
  Title18,
} from './styles';

type IUploadDocumentProps = {
  isDisabled?: boolean;
  hideTitle?: boolean;
  documentsList: DocumentType[];
  uploadedFiles?: IDocument[];
  setUploadedFiles: (uploadedFiles?: IDocument[]) => void;
};

export default function UploadDocument({
  isDisabled,
  hideTitle,
  documentsList,
  uploadedFiles,
  setUploadedFiles,
}: IUploadDocumentProps) {
  const inputFile = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  const [selectedDocument, setSelectedDocument] = useState<DocumentType | null>(
    null,
  );

  const onRemoveDocument = useCallback(
    (uploadKey: string) => {
      const updatedFiles = uploadedFiles?.filter(
        (doc) => doc.key !== uploadKey,
      );
      setUploadedFiles(updatedFiles);
    },
    [uploadedFiles, setUploadedFiles],
  );

  const onSendDocument = useCallback(
    async (document: DocumentType) => {
      setSelectedDocument(document);
      inputFile.current?.click();
    },
    [setSelectedDocument],
  );

  const onUploadDocument = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      if (uploadedFiles) {
        setUploadedFiles([
          ...uploadedFiles,
          {
            key: selectedDocument?.upload_key || undefined,
            file: file || undefined,
          },
        ]);
      } else {
        setUploadedFiles([
          {
            key: selectedDocument?.upload_key || undefined,
            file: file || undefined,
          },
        ]);
      }
    },
    [selectedDocument, uploadedFiles],
  );

  const columns: ColumnsType<DocumentType> = useMemo(() => {
    const items: ColumnsType<DocumentType> = [
      {
        render: (document: DocumentType) => (
          <span
            style={{
              color: theme.palette.text.primary,
              padding: '0',
              fontSize: '1rem',
            }}
          >{`${document.label}`}</span>
        ),
        width: '40%',
      },
    ];

    items.push({
      align: 'end',
      width: '60%',
      render: (document: DocumentType) => {
        const findKey = uploadedFiles?.find(
          (doc) => doc.key === document.upload_key,
        );

        if (findKey) {
          return (
            <StyledDownloadButton>
              <span>{findKey.file?.name}</span>
              <Button
                type="link"
                onClick={() => saveAs(findKey.file as File, findKey.file?.name)}
              >
                <NewDownloadIcon
                  size={20}
                  color={theme.palette.brand.primary[500]}
                />
              </Button>
              <Button
                type="link"
                onClick={() => onRemoveDocument(document.upload_key)}
                style={{ padding: '0' }}
              >
                <NewTrashIcon
                  size={20}
                  color={theme.palette.brand.primary[500]}
                />
              </Button>
            </StyledDownloadButton>
          );
        }

        return (
          <StyledButton>
            <Button
              type="dashed"
              disabled={isDisabled}
              onClick={() => onSendDocument(document)}
              style={{
                borderRadius: '16px',
                padding: '1.2rem 1rem',
              }}
            >
              <UploadCloudIcon
                size={21}
                color={theme.palette.brand.primary[500]}
              />
              <span color={theme.palette.primary.main}>Fazer upload</span>
            </Button>
          </StyledButton>
        );
      },
    });

    return items;
  }, [isDisabled, selectedDocument, uploadedFiles]);

  return (
    <>
      {!hideTitle && (
        <StepHeader>
          <Title18>Documentos</Title18>
        </StepHeader>
      )}
      <StyledTableWrapper>
        <Table
          columns={columns}
          rowKey={(record) => record.label}
          dataSource={documentsList}
          pagination={false}
        />
      </StyledTableWrapper>
      <input
        type="file"
        id="file"
        ref={inputFile}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        onChange={onUploadDocument}
        style={{ display: 'none' }}
      />
    </>
  );
}
